import type { Environment } from '~/engine/data/Environment';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import ContourSuggestionActions from './ContourSuggestionActions';
import TileAdjacentSliceGhostLegendMount from './TileAdjacentSliceGhostLegendMount';
import TileAnnotationMount from './TileAnnotationMount';
import TileCouchTopLineMount from './TileCouchTopLineMount';
import TileCrosshairMount from './TileCrosshairMount';
import TileDoseColorwashMount from './TileDoseColorwashMount';
import TileImageOutlineMount from './TileImageOutlineMount';
import TilePetColorwashMount from './TilePetColorwashMount';
import TilePetMaxMarkerMount from './TilePetMaxMarkerMount';
import TilePoiInteractionMount from './TilePoiInteractionMount';
import TilePoiMount from './TilePoiMount';
import TileRegistrationHintMount from './TileRegistrationHintMount';
import TileVoiMount from './TileVoiMount';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';

interface TileEngineOverlaysProps {
    renderer: RenderAdapter;
    /** The images the tile leads, as series instance uids. */
    ledImages: ReadonlyArray<SeriesInstanceUid>;
    /** The Environment whose tiles host the shared VOI box, resolved once per grid. */
    voiHost: Environment | undefined;
    /** The Environment whose tiles draw the SUV maximum marker, resolved once per grid. */
    petMaxMarkerHost: Environment | undefined;
    /**
     * The Environment of the edit target, resolved once per grid: its tiles
     * draw the couch-top line, show the adjacent-slice ghost legend and
     * carry the accept and decline buttons of a contour suggestion.
     */
    editTargetHost: Environment | undefined;
    /**
     * The Environment the registration tool moves the image of, while it
     * can and the registration step is open, resolved once per grid: its
     * tiles name the registration keys.
     */
    registrationHintHost: Environment | undefined;
}

/**
 * Composes the production per-tile overlays (image outline, crosshair, annotations, points
 * of interest, the VOI box, the dose and PET colorwash controls, the SUV
 * maximum marker, the couch-top line, the adjacent-slice ghost legend, the
 * contour suggestion's accept and decline buttons, the registration key
 * hint) so a host's tileOverlay callback renders one element:
 *
 *     (context) => (
 *         <TileEngineOverlays
 *             renderer={context.renderer}
 *             ledImages={context.ledImages}
 *             voiHost={voiHost}
 *             petMaxMarkerHost={petMaxMarkerHost}
 *             editTargetHost={editTargetHost}
 *             registrationHintHost={registrationHintHost}
 *         />
 *     )
 */
export default function TileEngineOverlays({
    renderer,
    ledImages,
    voiHost,
    petMaxMarkerHost,
    editTargetHost,
    registrationHintHost,
}: TileEngineOverlaysProps) {
    return (
        <>
            <TileImageOutlineMount renderer={renderer} />
            <TileCrosshairMount renderer={renderer} />
            <TileAnnotationMount renderer={renderer} />
            <TilePoiMount renderer={renderer} />
            <TilePoiInteractionMount renderer={renderer} />
            <TileVoiMount renderer={renderer} voiHost={voiHost} />
            <TileDoseColorwashMount renderer={renderer} ledImages={ledImages} />
            <TilePetColorwashMount renderer={renderer} ledImages={ledImages} />
            <TilePetMaxMarkerMount
                renderer={renderer}
                markerHost={petMaxMarkerHost}
            />
            <TileCouchTopLineMount
                renderer={renderer}
                couchHost={editTargetHost}
            />
            <TileAdjacentSliceGhostLegendMount
                renderer={renderer}
                legendHost={editTargetHost}
            />
            <ContourSuggestionActions
                renderer={renderer}
                suggestionHost={editTargetHost}
            />
            <TileRegistrationHintMount
                renderer={renderer}
                hintHost={registrationHintHost}
            />
        </>
    );
}
